import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useAPI } from "../contexts/APIContext";
import toast from "react-hot-toast";

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

// Verifiable-parental-consent notice, shown full-screen ONCE after a
// successful trial/subscription purchase (the card charge is the COPPA
// "verification" step) and before the app becomes usable. Mirrors the iOS
// NOParentalConsentStep copy, with the service-provider list adapted to the
// web stack. Persists to users.personalization.parental_consent via the same
// PATCH the iOS app uses, so accounts that already consented on iOS never
// see this screen (and vice versa).
// Desktop: two-column layout sized to fit one screen without scrolling;
// stacks (and scrolls) on mobile.
// Inline stroke icons matching the iOS SF Symbols (person.fill, camera.fill,
// paintbrush, waveform, chart.bar) — the codebase has no icon library.
const icon = (paths) => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#0065F8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {paths}
  </svg>
);

const COLLECTED = [
  {
    icon: icon(
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M4.5 20c1.4-3.2 4.2-5 7.5-5s6.1 1.8 7.5 5" />
      </>
    ),
    title: "First name & age",
    detail: "to personalize their stories.",
  },
  {
    icon: icon(
      <>
        <path d="M3.5 8.5A2.5 2.5 0 016 6h2l1.5-2h5L16 6h2a2.5 2.5 0 012.5 2.5v8A2.5 2.5 0 0118 19H6a2.5 2.5 0 01-2.5-2.5z" />
        <circle cx="12" cy="12.5" r="3.5" />
      </>
    ),
    title: "A photo (if you add one)",
    detail:
      "used once to create their cartoon character, then deleted. We don't keep the photo.",
  },
  {
    icon: icon(
      <>
        <path d="M18.5 3.5l2 2L11 15l-3 1 1-3z" />
        <path d="M8 16c-1.5.5-2.5 1.5-3 3.5 1.8 0 3.2-.5 4-2" />
      </>
    ),
    title: "Drawings, stories & characters",
    detail: "to save and show their creations.",
  },
  {
    icon: icon(
      <>
        <path d="M3.5 10v4M7.5 7v10M11.5 4v16M15.5 8v8M19.5 10v4" />
      </>
    ),
    title: "Voice recordings",
    detail:
      "when your child speaks a story idea — to turn their speech into text — or when a parent records their voice for storybooks.",
  },
  {
    icon: icon(
      <>
        <path d="M4 20V12M10 20V6M16 20v-9M21 20H3.5" />
      </>
    ),
    title: "How Pixley is used",
    detail:
      "which screens and features get opened — so we can fix problems and make the app better.",
  },
];

const PARAGRAPHS = [
  "We use this information only to run and personalize Pixley. We never use your child's information for advertising, and we never sell it.",
  "We share it only with trusted service providers that help us run the app: Google and Anthropic (create the cartoons and write the stories), OpenAI (writes character descriptions and turns speech into text), ElevenLabs (character and storybook voices), Sightengine (content-safety checks), mem0 (remembers what your child likes so their stories stay personal), PostHog (product analytics), Stripe and RevenueCat (payments and subscriptions), Resend (account emails), and Supabase and Amazon Web Services (secure hosting). They may only use it to provide those services to us — nothing else.",
  "Our product analytics record only which screens and features are used — never your child's name, photo, drawings, stories, or voice. They're linked to your account so we can see things like whether families come back, and they are never used for advertising.",
  "You're always in control. You can review or delete your child's information, or withdraw consent, at any time by emailing team@pixleyai.com.",
];

const ParentalConsentGate = () => {
  const { user, updateUser } = useAuth();
  const { apiClient } = useAPI();
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleAgree = async () => {
    if (!agreed || saving) return;
    setSaving(true);
    const consent = { agreed: true, agreed_at: new Date().toISOString() };
    try {
      await apiClient.updateUserPersonalization(user.id, {
        parental_consent: consent,
      });
      // Reflect it locally so the gate unmounts without a reload
      updateUser({
        ...user,
        personalization: {
          ...(user.personalization || {}),
          parental_consent: consent,
        },
      });
      // The first-story auto-queue lives in EntitlementGateContext and fires
      // on the personalization update above — the gate stays consent-only
      // (it's skipped entirely for already-consented accounts, so it could
      // never own the queueing).
      localStorage.removeItem("pixley_post_onboarding_nav");
    } catch (e) {
      toast.error("Couldn't save — please try again.");
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] overflow-y-auto"
      style={{
        ...FREDOKA,
        background: "linear-gradient(180deg, #F5FAFF 0%, #E8F1FF 100%)",
      }}
    >
      <div className="min-h-full flex flex-col justify-center w-full max-w-6xl mx-auto px-6 py-6">
        {/* Header: mascot beside the title keeps it one compact row */}
        <div className="flex items-center gap-4">
          <img
            src="/assets/pixley-panda-nobg.png"
            alt=""
            className="hidden sm:block"
            style={{ height: 64, flexShrink: 0 }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <div>
            <div
              style={{
                color: "#0065F8",
                fontWeight: 700,
                fontSize: 13,
                letterSpacing: 2,
                textTransform: "uppercase",
              }}
            >
              Parental Consent
            </div>
            <h1
              style={{
                color: "#0B1A33",
                fontWeight: 700,
                fontSize: "clamp(22px, 2.4vw, 28px)",
                lineHeight: 1.15,
                marginTop: 4,
              }}
            >
              A quick note for <span style={{ color: "#0065F8" }}>grown-ups</span>
            </h1>
            <p style={{ color: "#5A6B85", fontSize: 15, marginTop: 4 }}>
              Pixley is made for kids, so before your child starts, we need a
              parent or guardian's OK.
            </p>
          </div>
        </div>

        {/* Two columns: collection card | disclosure */}
        <div className="grid lg:grid-cols-2 gap-5 items-start" style={{ marginTop: 18 }}>
          <div
            style={{
              background: "#FFFFFF",
              borderRadius: 18,
              border: "1px solid #E8EDF5",
              boxShadow: "0 5px 10px rgba(0,0,0,0.05)",
              padding: 16,
            }}
          >
            <div
              style={{
                color: "#0B1A33",
                fontWeight: 700,
                fontSize: 16,
                marginBottom: 10,
              }}
            >
              Here's what Pixley collects from your child, and why:
            </div>
            <div className="flex flex-col gap-2.5">
              {COLLECTED.map((row) => (
                <div key={row.title} className="flex items-start gap-3">
                  <div
                    className="flex-shrink-0 flex items-center justify-center"
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      background: "#E3F0FF",
                    }}
                  >
                    {row.icon}
                  </div>
                  <p style={{ color: "#5A6B85", fontSize: 13.5, marginTop: 1, lineHeight: 1.45 }}>
                    <span style={{ color: "#0B1A33", fontWeight: 600 }}>
                      {row.title}
                    </span>{" "}
                    — {row.detail}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2.5">
            {PARAGRAPHS.map((p, i) => (
              <p key={i} style={{ color: "#5A6B85", fontSize: 13.5, lineHeight: 1.5 }}>
                {p}
              </p>
            ))}
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#0065F8",
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              Read our full Privacy Policy ↗
            </a>
          </div>
        </div>

        {/* Consent row: checkbox + CTA side by side */}
        <div className="flex flex-col sm:flex-row gap-3 items-stretch" style={{ marginTop: 18 }}>
          <button
            type="button"
            onClick={() => setAgreed(!agreed)}
            className="flex-1 text-left"
            style={{
              background: "#FFFFFF",
              borderRadius: 16,
              border: agreed ? "2px solid #0065F8" : "1px solid #E8EDF5",
              boxShadow: "0 4px 8px rgba(0,0,0,0.05)",
              padding: "12px 14px",
              display: "flex",
              alignItems: "center",
              gap: 12,
              cursor: "pointer",
            }}
          >
            <span
              className="flex-shrink-0 flex items-center justify-center"
              style={{
                width: 24,
                height: 24,
                borderRadius: 7,
                background: agreed ? "#0065F8" : "#FFFFFF",
                border: agreed ? "2px solid #0065F8" : "2px solid #DDE6F2",
                color: "#FFFFFF",
                fontSize: 13,
                fontWeight: 800,
                transition: "all 0.18s ease-out",
              }}
            >
              {agreed ? "✓" : ""}
            </span>
            <span style={{ color: "#0B1A33", fontSize: 13.5, fontWeight: 500, lineHeight: 1.4 }}>
              I am this child's parent or legal guardian, and I consent to
              Pixley collecting and using my child's information as described
              above.
            </span>
          </button>

          <button
            type="button"
            onClick={handleAgree}
            disabled={!agreed || saving}
            className="active:scale-[0.97] sm:self-stretch"
            style={{
              background: "#0065F8",
              opacity: agreed && !saving ? 1 : 0.45,
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: 16,
              padding: "14px 34px",
              borderRadius: 16,
              boxShadow:
                agreed && !saving ? "0 8px 16px rgba(0,101,248,0.35)" : "none",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              whiteSpace: "nowrap",
              ...FREDOKA,
            }}
          >
            {saving ? "Saving…" : "I agree — continue"}
            {!saving && <span aria-hidden="true">›</span>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ParentalConsentGate;
